import { DataSource } from '@/types/DataSource';
import { fetchDataSources } from '@/apis/fetchDataSources';
import { create } from 'zustand';
import headerObject from '@/types/headerObj';
import useUserContextStore from '@/contexts/UserContext';

interface DataSourceState {
  dataSources: DataSource[];
  getDataSources: (orgUUID: string, userUUID: string) => Promise<void>;
  updateDataSource: (dataSource: DataSource) => Promise<void>;
  removeDataSource: (datasourceUUID: string) => void;
  skipNextRefresh: boolean;
  setSkipNextRefresh: (skip: boolean) => void;
}

/**
 * In-flight requests keyed by org + user. Several call sites (the InitialSpinner
 * bootstrap effect, Documentation, Integrations) invoke getDataSources on the
 * same mount, and each one used to fire its own identical fetchDatasources/ POST
 * (observed: 4 concurrent copies of an 85 kB response on one page load).
 * Concurrent callers now share a single request.
 */
const inFlightRequests = new Map<string, Promise<void>>();

export const useDataSourceStore = create<DataSourceState>((set, get) => ({
  dataSources: [],
  skipNextRefresh: false,
  setSkipNextRefresh: (skip: boolean) => set({ skipNextRefresh: skip }),
  getDataSources: async (orgUUID: string, userUUID: string) => {
    if (get().skipNextRefresh) {
      set({ skipNextRefresh: false });
      return;
    }

    const requestKey = `${orgUUID}|${userUUID}`;
    const pending = inFlightRequests.get(requestKey);
    if (pending) return pending;

    const request = fetchAndStore(orgUUID, userUUID, set, get).finally(() => {
      inFlightRequests.delete(requestKey);
    });
    inFlightRequests.set(requestKey, request);
    return request;
  },
  updateDataSource: async (dataSource: DataSource) => {
    const dataSources = get().dataSources;
    const index = dataSources.findIndex(
      (ds) =>
        ds.datasource?.DatasourceUUID === dataSource.datasource?.DatasourceUUID
    );

    if (index === -1) return;

    const updatedDataSources = [...dataSources];
    updatedDataSources[index] = dataSource;

    set({ dataSources: updatedDataSources });
  },
  removeDataSource: (datasourceUUID: string) => {
    const currentDataSources = get().dataSources;

    if (currentDataSources.length === 1) {
      set({ skipNextRefresh: true });
    }

    const filteredDataSources = currentDataSources.filter(
      (ds) => ds.datasource?.DatasourceUUID !== datasourceUUID
    );

    set({ dataSources: filteredDataSources });
  },
}));

type SetState = (partial: Partial<DataSourceState>) => void;
type GetState = () => DataSourceState;

async function fetchAndStore(
  orgUUID: string,
  userUUID: string,
  set: SetState,
  get: GetState
): Promise<void> {
  const user = useUserContextStore.getState().user;

  const headerObj = {
    Useruuid: userUUID,
    Orguuid: orgUUID,
    Authorization: `Bearer ${user?.accessToken}`,
  } as headerObject;

  try {
    const [dataSourcesForOrgs] = await Promise.all([
      orgUUID ? fetchDataSources({ orgUUID, headerObj }) : Promise.resolve([]),
    ]);

    const datasources: DataSource[] = [];

    if (dataSourcesForOrgs?.Datasources && dataSourcesForOrgs.Error === null) {
      datasources.push(...dataSourcesForOrgs.Datasources);
    }

    const currentDataSources = get().dataSources;
    if (datasources.length === 0 && currentDataSources.length > 0) {
      return;
    }

    const uniqueDatasources = datasources.filter(
      (dataSource, index, self) =>
        index ===
        self.findIndex(
          (t) =>
            t.datasource?.DatasourceUUID ===
            dataSource.datasource?.DatasourceUUID
        )
    );

    set({ dataSources: uniqueDatasources });
  } catch (error) {
    if (get().dataSources.length > 0) {
      console.error(
        '[DataSourceStore] Error during refresh, maintaining current state',
        error
      );
    }
  }
}
